import { watch } from 'vue';
import { useGridPreferencesStore } from '@/stores/gridPreferences';
import { onlyTableBodyChanged } from '@/utils/layoutReads';

/**
 * `v-resizable-cols` — user-resizable, per-grid persisted column widths for any
 * Buefy `<b-table>`.
 *
 * Binding value: `{ gridKey, fields?, defaultWidths? }`
 *   - gridKey:       persistence key (the grid's resource slug, e.g. 'contacts')
 *   - fields:        ordered field keys for the data columns, so a stored width
 *                    survives column reordering. Falls back to positional keys.
 *   - defaultWidths: field => px seed widths (a page's configured column widths),
 *                    used until the user drags a column.
 *
 * How it works: the table is switched to `table-layout: fixed` (via the
 * `is-resizable` class) and every data column gets an explicit `<th>` width —
 * from the user's saved width, else the page default, else the width Buefy
 * already rendered. A grip on each header's right edge drags that one column and
 * saves it. Buefy re-renders on data load, so a MutationObserver re-applies.
 *
 * Works uniformly on BaseList's full-width grid and BaseTable's grids because it
 * reads the rendered DOM rather than the column declarations.
 */

// Floor a column can be dragged to — below this, headers/content start to
// overlap their neighbours.
const MIN_WIDTH = 80;

// Width given to a flexible (un-configured) column once the grid is in "manual"
// mode — i.e. the user has resized at least one column, so every column must be
// pinned to stop the flexible ones collapsing under the widened one.
const MANUAL_FLEX_WIDTH = 150;

// $desktop in variables.scss — below it the grid pane is the whole screen and a
// wide grid is read by scrolling it (see `applyMinWidth`).
const DESKTOP_WIDTH = 1024;

// Ceiling for a single column, as a share of the grid's visible width.
//
// Cell text truncates (`.inline-cell-value` is nowrap + ellipsis), but that only
// bounds a column while the table is in `table-layout: fixed`. Measured outside
// that — a first paint, a grid this directive does not drive — a nowrap cell is
// as wide as its longest string, and pinning captured that: one 1735px
// "description" column, saved, dragged the whole grid past the screen on every
// later visit. A column past this share is that accident, never a reading
// choice, so it is clamped on the way in *and* on the way out — which also heals
// a layout already saved that way.
const MAX_WIDTH_RATIO = 0.6;

// …with a floor, so the clamp never bites on a genuinely narrow grid (a preview
// panel, a phone) where 60% is a handful of pixels.
const MAX_WIDTH_FLOOR = 480;

function resolveOptions(binding) {
    const value = binding.value || {};
    if (typeof value === 'string') {
        return { gridKey: value, fields: [], defaultWidths: {} };
    }
    return {
        gridKey: value.gridKey || '',
        fields: value.fields || [],
        defaultWidths: value.defaultWidths || {}
    };
}

class ColumnResizer {
    constructor(el, options) {
        this.el = el;
        this.optionsSignature = JSON.stringify([options.gridKey, options.fields || [], options.defaultWidths || {}]);
        this.setOptions(options);
        this.store = useGridPreferencesStore();
        this.dragging = false;
        this.appliedTable = null;
        this.appliedWidths = [];

        this.onDomChange = this.onDomChange.bind(this);
        this.observer = new MutationObserver(this.onDomChange);
        this.observer.observe(el, { childList: true, subtree: true });

        // Re-apply whenever the saved widths change — the initial load resolving,
        // a reset from the toolbar, or a change in another tab.
        this.stopWatch = watch(
            () => this.store.getColumnWidths(this.gridKey),
            () => {
                if (!this.dragging) {
                    this.sync();
                }
            },
            { deep: true }
        );

        // The grid's minimum width depends on how much room the pane has (see
        // `applyMinWidth`), so a window that changes size has to recompute it —
        // otherwise a laptop docked to a monitor, or a tablet turned on its side,
        // keeps the floor measured for the other screen.
        this.onWindowResize = () => {
            if (!this.dragging) {
                this.applyMinWidth(this.resolveWidths());
            }
        };
        window.addEventListener('resize', this.onWindowResize);

        if (this.gridKey) {
            this.store.loadPreferences(this.gridKey);
        }
        this.sync();
    }

    setOptions(options) {
        this.gridKey = options.gridKey;
        this.fields = options.fields || [];
        this.defaultWidths = options.defaultWidths || {};
    }

    // The directive's `updated` hook runs on every render of the host — each
    // page of rows a list appends. Only a change of the options themselves is
    // worth a sync; a change of the header is caught by the observer.
    update(options) {
        const signature = JSON.stringify([options.gridKey, options.fields || [], options.defaultWidths || {}]);
        if (signature === this.optionsSignature) {
            return;
        }
        this.optionsSignature = signature;
        this.setOptions(options);
        this.sync();
    }

    getTable() {
        return this.el.querySelector('table');
    }

    /**
     * The widest a single column may be, measured against what the grid can
     * actually show. See {@link MAX_WIDTH_RATIO}.
     */
    maxWidth() {
        const visible = this.getTable()?.parentElement?.clientWidth || 0;

        return Math.max(MAX_WIDTH_FLOOR, Math.round(visible * MAX_WIDTH_RATIO));
    }

    clamp(width) {
        return width == null ? width : Math.min(Math.max(MIN_WIDTH, width), this.maxWidth());
    }

    // Data columns are the header cells that carry a `.th-wrap` — this skips
    // Buefy's checkbox and detail-toggle cells regardless of their position.
    getDataThs() {
        const table = this.getTable();
        if (!table) {
            return [];
        }
        const headRow = table.querySelector('thead tr');
        if (!headRow) {
            return [];
        }
        return Array.from(headRow.children).filter(th => th.tagName === 'TH' && th.querySelector(':scope > .th-wrap'));
    }

    // Only the header carries widths. Rows arriving in the body — a page of
    // infinite scroll, a cell refreshing, the loader in the footer — change
    // nothing here, and syncing on them forced a layout of the whole table
    // (`applyMinWidth` measures) under the reader's scroll on every page.
    //
    // They can still come with a re-render of the table that wiped what sync
    // wrote — Vue rewrites the `<table>` class whenever Buefy's binding moves —
    // so a body change checks, without measuring anything, that it all held.
    onDomChange(records) {
        if (this.dragging) {
            return;
        }
        if (onlyTableBodyChanged(records) && this.stillApplied()) {
            return;
        }
        this.sync();
    }

    stillApplied() {
        const table = this.getTable();
        if (!table || table !== this.appliedTable || !table.classList.contains('is-resizable')) {
            return false;
        }

        return this.appliedWidths.every(({ th, width }) => th.isConnected && th.style.width === width);
    }

    // Resolve a column's persistence key. When explicit `fields` are supplied
    // (BaseList), an entry of null/'' marks a non-data column (e.g. the checkbox
    // column) to leave untouched — signalled by returning null. Otherwise fall
    // back to a stable positional key.
    columnKey(th, index) {
        if (this.fields.length) {
            const field = this.fields[index];
            return field || null;
        }
        return th.dataset.colResizeKey || `idx-${index}`;
    }

    // The fixed width Buefy gave the column via its `width` prop (e.g. the 40px
    // checkbox or a 100px action column), captured once — before we clear the
    // inner wrap's width — so it survives across syncs and a reset.
    naturalWidth(th) {
        if (th.__naturalWidth === undefined) {
            const wrap = th.querySelector(':scope > .th-wrap');
            th.__naturalWidth =
                wrap && wrap.style.width && wrap.style.width.endsWith('px') ? parseFloat(wrap.style.width) : null;
        }
        return th.__naturalWidth;
    }

    // The width to pin a column at before the user has touched it: the page's
    // configured default, else the fixed width Buefy already rendered. Returns
    // null for a "flexible" column with no configured width, so under the fixed
    // layout it shares the remaining space instead of being pinned.
    seedWidth(th, key) {
        if (key && this.defaultWidths[key] != null) {
            return this.defaultWidths[key];
        }
        return this.naturalWidth(th);
    }

    /**
     * Each data column paired with the width to pin it at — null for a column
     * left flexible. Empty when the grid is not ready (no table, one column).
     *
     * @return {Array<{th: HTMLElement, key: string, width: ?number}>}
     */
    resolveWidths() {
        if (!this.getTable()) {
            return [];
        }
        const dataThs = this.getDataThs();
        if (dataThs.length < 2) {
            return [];
        }

        const stored = this.store.getColumnWidths(this.gridKey);
        // Once any column is stored the grid is "manual": every column must be
        // pinned so a widened column scrolls the table instead of squeezing the
        // still-flexible ones (which would overlap — see MANUAL_FLEX_WIDTH).
        const manualMode = Object.keys(stored).length > 0;

        // A configured/stored width always pins the column; a flexible column
        // stays unpinned (shares remaining space) until the grid goes manual,
        // after which it gets a sane default.
        return dataThs.map((th, index) => {
            const key = this.columnKey(th, index);
            let width;
            if (key && stored[key] != null) {
                // Clamped rather than trusted: a stored width is only ever as
                // good as the measurement it came from.
                width = this.clamp(stored[key]);
            } else {
                width = this.seedWidth(th, key);
                if (width == null && key && manualMode) {
                    width = MANUAL_FLEX_WIDTH;
                }
            }
            return { th, key, width };
        });
    }

    sync() {
        const table = this.getTable();
        const applied = this.resolveWidths();
        if (!table || applied.length === 0) {
            return;
        }

        table.classList.add('is-resizable');
        this.appliedTable = table;
        this.appliedWidths = applied.map(({ th, width }) => ({ th, width: width != null ? `${width}px` : '' }));

        applied.forEach(({ th, key, width }) => {
            // A concrete width pins the column; null leaves it flexible so the
            // fixed layout distributes remaining space to it. Clearing the inline
            // width on a null column is what lets a reset restore the defaults.
            if (width != null) {
                th.style.width = `${width}px`;
                // Let the inner wrap fill the cell now that the width lives on the th.
                const wrap = th.querySelector(':scope > .th-wrap');
                if (wrap) {
                    wrap.style.width = '';
                }
            } else {
                th.style.width = '';
            }
            // Non-data columns (null key) are pinned but not draggable.
            if (!key) {
                return;
            }
            th.dataset.colResizeKey = key;
            if (!th.querySelector(':scope > .col-resize-grip')) {
                this.addGrip(th);
            }
        });

        this.applyMinWidth(applied);
    }

    // `table-layout: fixed` with `width: 100%` does not overflow: asked for more
    // width than the pane has, it scales every pinned column down instead —
    // which on a tablet crushed eight columns into headers overlapping their
    // neighbours. A floor equal to what the columns actually asked for turns
    // that squeeze back into the horizontal scroll the wrapper is there for.
    applyMinWidth(applied) {
        const table = this.getTable();
        if (!table || applied.length === 0) {
            return;
        }
        const fixed = Array.from(table.querySelectorAll('thead tr > th'))
            .filter(th => !th.querySelector(':scope > .th-wrap'))
            .reduce((sum, th) => sum + th.getBoundingClientRect().width, 0);
        // What a column with no configured width is worth in that total. A pane
        // narrower than a desktop is going to scroll whatever we do, so the
        // flexible columns may as well be readable; a desktop is expected to fit,
        // and pricing them at their comfortable width there would hand it a
        // scrollbar it does not need.
        const pane = table.parentElement?.clientWidth || 0;
        const flexFloor = pane > 0 && pane < DESKTOP_WIDTH ? MANUAL_FLEX_WIDTH : MIN_WIDTH;
        const total = applied.reduce((sum, { width }) => sum + (width ?? flexFloor), fixed);

        table.style.minWidth = `${Math.round(total)}px`;
    }

    addGrip(th) {
        const grip = document.createElement('div');
        grip.className = 'col-resize-grip';
        grip.addEventListener('pointerdown', event => this.startResize(event, th));
        // Keep the grip from triggering the header's sort click.
        grip.addEventListener('click', event => event.stopPropagation());
        th.appendChild(grip);
    }

    startResize(event, th) {
        event.preventDefault();
        event.stopPropagation();
        this.dragging = true;

        // Freeze every column at its current width first. Otherwise the columns
        // that are still flexible (no explicit width) would absorb the change and
        // collapse — their text overlapping neighbours. Once all columns are
        // pinned, dragging one widens the table (horizontal scroll) instead.
        this.pinAllColumns();

        const startX = event.clientX;
        const startWidth = th.getBoundingClientRect().width;

        const prevCursor = document.body.style.cursor;
        const prevUserSelect = document.body.style.userSelect;
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';

        const onMove = moveEvent => {
            const dx = moveEvent.clientX - startX;
            th.style.width = `${this.clamp(Math.round(startWidth + dx))}px`;
        };

        const onUp = () => {
            document.removeEventListener('pointermove', onMove);
            document.removeEventListener('pointerup', onUp);
            document.body.style.cursor = prevCursor;
            document.body.style.userSelect = prevUserSelect;
            this.dragging = false;
            this.persist();
        };

        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', onUp);
    }

    // Give every draggable column an explicit width equal to what it currently
    // renders at (floored at the minimum), converting any still-flexible column to
    // fixed. Non-draggable columns (the checkbox / action column, keyless) are
    // left at the fixed width sync already gave them — the minimum floor must not
    // stretch the narrow checkbox column.
    pinAllColumns() {
        this.getDataThs().forEach(th => {
            if (!th.dataset.colResizeKey) {
                return;
            }
            const width = this.clamp(Math.round(th.getBoundingClientRect().width));
            th.style.width = `${width}px`;
            const wrap = th.querySelector(':scope > .th-wrap');
            if (wrap) {
                wrap.style.width = '';
            }
        });
    }

    // Save every data column's width, so a resized grid stays fully pinned (and
    // never collapses a neighbour) across reloads.
    persist() {
        if (!this.gridKey) {
            return;
        }
        const widths = { ...this.store.getColumnWidths(this.gridKey) };
        this.getDataThs().forEach(th => {
            const key = th.dataset.colResizeKey;
            if (key) {
                widths[key] = this.clamp(Math.round(th.getBoundingClientRect().width));
            }
        });
        this.store.saveColumnWidths(this.gridKey, widths);
    }

    destroy() {
        this.observer.disconnect();
        window.removeEventListener('resize', this.onWindowResize);
        if (this.stopWatch) {
            this.stopWatch();
        }
    }
}

export const resizableCols = {
    mounted(el, binding) {
        el.__columnResizer = new ColumnResizer(el, resolveOptions(binding));
    },
    updated(el, binding) {
        if (el.__columnResizer) {
            el.__columnResizer.update(resolveOptions(binding));
        }
    },
    unmounted(el) {
        if (el.__columnResizer) {
            el.__columnResizer.destroy();
            delete el.__columnResizer;
        }
    }
};
